import * as tf from "@tensorflow/tfjs";
import { renderBoxes } from "./renderBox";
import logger from "./logger";
import { COUNTRY_MODEL_MAP, DEFAULT_COUNTRY_KEY, LABEL_FILES } from "src/constants/countryConfig";

// const numClass = labels.length;

/**
 * Preprocess image / frame before forwarded into the model
 * @param {HTMLVideoElement|HTMLImageElement} source
 * @param {Number} modelWidth
 * @param {Number} modelHeight
 * @returns input tensor, xRatio and yRatio
 */
const preprocess = (source, modelWidth, modelHeight) => {
    let xRatio, yRatio; // ratios for boxes

    const input = tf.tidy(() => {
        const img = tf.browser.fromPixels(source);

        // padding image to square => [n, m] to [n, n], n > m
        const [h, w] = img.shape.slice(0, 2); // get source width and height
        const maxSize = Math.max(w, h); // get max size
        const imgPadded = img.pad([
            [0, maxSize - h], // padding y [bottom only]
            [0, maxSize - w], // padding x [right only]
            [0, 0],
        ]);

        xRatio = maxSize / w; // update xRatio
        yRatio = maxSize / h; // update yRatio

        return tf.image
            .resizeBilinear(imgPadded, [modelWidth, modelHeight]) // resize frame
            .div(255.0) // normalize
            .expandDims(0); // add batch
    });

    return [input, xRatio, yRatio];
};

/**
 * Function run inference and do detection from source.
 * @param {HTMLImageElement|HTMLVideoElement} source
 * @param {tf.GraphModel} model loaded YOLOv8 tensorflow.js model
 * @param {HTMLCanvasElement} canvasRef canvas reference
 * @param {Array} scores_data scores array
 * @param {Array} classes_data class array
 * @param {VoidFunction} callback function to run after detection process
 */
export const detect = async (source, model, canvasRef, carrierObj, callback = () => { }) => {
    const { country } = carrierObj
    const labelKey = COUNTRY_MODEL_MAP[country] || DEFAULT_COUNTRY_KEY;
    const generalLabels = LABEL_FILES[labelKey];
    const numClass = generalLabels.length;

    const [modelWidth, modelHeight] = model.inputShape.slice(1, 3); // get model width and height
    let threshold = 0.4
    tf.engine().startScope(); // start scoping tf engine
    const [input, xRatio, yRatio] = preprocess(source, modelWidth, modelHeight); // preprocess image
    try {
        const res = model.net.execute(input); // inference model

        const transRes = res.transpose([0, 2, 1]); // transpose result [b, det, n] => [b, n, det]

        const boxes = tf.tidy(() => {
            const w = transRes.slice([0, 0, 2], [-1, -1, 1]); // get width
            const h = transRes.slice([0, 0, 3], [-1, -1, 1]); // get height
            const x1 = tf.sub(transRes.slice([0, 0, 0], [-1, -1, 1]), tf.div(w, 2)); // x1
            const y1 = tf.sub(transRes.slice([0, 0, 1], [-1, -1, 1]), tf.div(h, 2)); // y1
            return tf
                .concat(
                    [
                        y1,
                        x1,
                        tf.add(y1, h), //y2
                        tf.add(x1, w), //x2
                    ],
                    2
                )
                .squeeze();
        }); // process boxes [y1, x1, y2, x2]

        const [scores, classes] = tf.tidy(() => {
            // class scores
            const rawScores = transRes.slice([0, 0, 4], [-1, -1, numClass]).squeeze(0); // #6 only squeeze axis 0 to handle only 1 class models
            return [rawScores.max(1), rawScores.argMax(1)];
        }); // get max scores and classes index

        const nms = await tf.image.nonMaxSuppressionAsync(boxes, scores, 1, 0.2, 0.6); // NMS to filter boxes - only best detection for low-performance devices

        const boxes_data = boxes.gather(nms, 0).dataSync(); // indexing boxes by nms index
        const scores_data = scores.gather(nms, 0).dataSync(); // indexing scores by nms index
        const classes_data = classes.gather(nms, 0).dataSync(); // indexing classes by nms index
        if (carrierObj.process === "uploadPhoto") {
            return calculateScoresAndClasses(scores_data, classes_data, threshold, generalLabels);
        }

        if (carrierObj.process === "takePhoto") {
            renderBoxes(canvasRef, boxes_data, scores_data, classes_data, [xRatio, yRatio], threshold, carrierObj, generalLabels); // render boxes
        }
        tf.dispose([res, transRes, boxes, scores, classes, nms]); // clear memory

        callback();

        tf.engine().endScope(); // end of scoping
    } catch (error) {

    }
};

/**
 * Function to detect video from every source.
 * @param {HTMLVideoElement} vidSource video source
 * @param {tf.GraphModel} model loaded YOLOv8 tensorflow.js model
 * @param {HTMLCanvasElement} canvasRef canvas reference
 */
export const detectVideo = (vidSource, model, canvasRef, carrierObj) => {
    /**
     * Function to detect every frame from video
     */
    const detectFrame = async () => {
        if (vidSource.videoWidth === 0 || vidSource.srcObject === null) {
            const ctx = canvasRef.getContext("2d");
            ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height); // clean canvas
            return; // handle if source is closed
        }

        detect(vidSource, model, canvasRef, carrierObj, () => {
            requestAnimationFrame(detectFrame); // get another frame
        });
    };

    detectFrame(); // initialize to detect every frame
};



export const calculateScoresAndClasses = (scores_data, classes_data, threshold, labels) => {

    for (let i = 0; i < scores_data.length; ++i) {
        if (scores_data[i] > threshold) {

            // filter based on class threshold
            const klass = labels[classes_data[i]];
            logger.log("klass: " + klass);
            logger.log("score: " + (scores_data[i] * 100).toFixed(1));

            return {
                klass: klass,
                score: (scores_data[i] * 100).toFixed(1)
            }



        }
    }
}